import { BUILTIN_LOCALE_BUNDLES, BUILTIN_LOCALES } from "./locales";
import type {
    CreateI18nOptions,
    I18n,
    LocaleBundles,
    LocaleDirection,
    LocaleParams,
    ResolveLocaleOptions,
} from "./types";

export type {
    CreateI18nOptions,
    I18n,
    LocaleBundle,
    LocaleBundles,
    LocaleDirection,
    LocaleMessages,
    LocaleParams,
    ResolveLocaleOptions,
} from "./types";
export { BUILTIN_LOCALE_BUNDLES, BUILTIN_LOCALES } from "./locales";

export const AUTO_LOCALE = "auto";
export const DEFAULT_LOCALE = "zh-CN";

let activeI18n: I18n | null = null;

const STATIC_MESSAGE_REF_RE = /^%([^%\s]+)%$/;
const RTL_LANGUAGE_CODES = new Set([
    "ar",
    "fa",
    "he",
    "ps",
    "ur",
]);

export function normalizeLocale(value: string): string {
    const trimmed = value.trim().replace(/_/g, "-");
    if (!trimmed) return "";
    if (trimmed.toLowerCase() === AUTO_LOCALE) return AUTO_LOCALE;

    return trimmed
        .split("-")
        .filter(Boolean)
        .map((part, index) => {
            if (index === 0) return part.toLowerCase();
            if (part.length === 2 || /^\d{3}$/.test(part)) return part.toUpperCase();
            if (part.length === 4) {
                return `${part[0]?.toUpperCase() ?? ""}${part.slice(1).toLowerCase()}`;
            }
            return part.toLowerCase();
        })
        .join("-");
}

export function resolveLocale({
    requestedLocale,
    systemLocales = [],
    availableLocales = BUILTIN_LOCALES,
    fallbackLocale = DEFAULT_LOCALE,
}: ResolveLocaleOptions = {}): string {
    const available = availableLocales.map(normalizeLocale).filter(Boolean);
    const normalizedFallback = normalizeLocale(fallbackLocale);
    const fallback = available.includes(normalizedFallback)
        ? normalizedFallback
        : (available[0] ?? DEFAULT_LOCALE);
    const requested = normalizeLocale(requestedLocale ?? AUTO_LOCALE);
    const candidates =
        requested === AUTO_LOCALE || !requested
            ? systemLocales.map(normalizeLocale)
            : [requested];

    for (const candidate of candidates) {
        const matched = matchAvailableLocale(candidate, available);
        if (matched) return matched;
    }

    return fallback;
}

export function getLocaleDirection(locale: string): LocaleDirection {
    const language = normalizeLocale(locale).split("-")[0] ?? "";
    return RTL_LANGUAGE_CODES.has(language) ? "rtl" : "ltr";
}

export function formatMessage(message: string, params?: LocaleParams): string {
    if (!params) return message;

    return message.replace(/\{([\w.-]+)\}/g, (match, token: string) => {
        const value = Array.isArray(params)
            ? params[Number(token)]
            : (params as Record<string, unknown>)[token];
        return value === undefined || value === null ? match : String(value);
    });
}

export function getStaticMessageKey(value: string): string | null {
    const match = STATIC_MESSAGE_REF_RE.exec(value.trim());
    return match?.[1] ?? null;
}

export function createI18n({
    requestedLocale = AUTO_LOCALE,
    systemLocales = [],
    bundles = BUILTIN_LOCALE_BUNDLES,
    fallbackLocale = DEFAULT_LOCALE,
}: CreateI18nOptions = {}): I18n {
    const availableLocales = Object.keys(bundles);
    const locale = resolveLocale({
        requestedLocale,
        systemLocales,
        availableLocales,
        fallbackLocale,
    });
    const fallback = resolveLocale({
        requestedLocale: fallbackLocale,
        availableLocales,
        fallbackLocale: DEFAULT_LOCALE,
    });
    const requested = normalizeLocale(requestedLocale ?? AUTO_LOCALE) || AUTO_LOCALE;

    const lookup = (namespace: string, key: string): string | undefined =>
        lookupMessage(bundles, locale, fallback, namespace, key);

    return {
        requestedLocale: requested,
        locale,
        fallbackLocale: fallback,
        direction: getLocaleDirection(locale),
        t(namespace, key, params) {
            const message = lookup(namespace, key);
            return message === undefined ? key : formatMessage(message, params);
        },
        has(namespace, key) {
            return lookup(namespace, key) !== undefined;
        },
        resolveStaticText(value, params) {
            const ref = getStaticMessageKey(value);
            if (!ref) return value;

            const parsed = parseMessageKey(ref);
            if (!parsed) return value;

            const message = lookup(parsed.namespace, parsed.key);
            return message === undefined ? value : formatMessage(message, params);
        },
    };
}

export function setActiveI18n(i18n: I18n | null): void {
    activeI18n = i18n;
}

export function getActiveI18n(): I18n {
    return activeI18n ?? createI18n();
}

export function t(
    namespace: string,
    key: string,
    params?: LocaleParams,
): string {
    return getActiveI18n().t(namespace, key, params);
}

export function resolveStaticText(
    value: string,
    options: CreateI18nOptions & { params?: LocaleParams } = {},
): string {
    return createI18n(options).resolveStaticText(value, options.params);
}

function matchAvailableLocale(
    candidate: string,
    availableLocales: readonly string[],
): string | null {
    if (!candidate) return null;
    if (availableLocales.includes(candidate)) return candidate;

    const language = candidate.split("-")[0];
    if (!language) return null;
    return (
        availableLocales.find((locale) => locale.split("-")[0] === language) ??
        null
    );
}

function lookupMessage(
    bundles: LocaleBundles,
    locale: string,
    fallbackLocale: string,
    namespace: string,
    key: string,
): string | undefined {
    return (
        bundles[locale]?.[namespace]?.[key] ??
        bundles[fallbackLocale]?.[namespace]?.[key]
    );
}

function parseMessageKey(
    ref: string,
): { namespace: string; key: string } | null {
    const dot = ref.indexOf(".");
    if (dot <= 0 || dot === ref.length - 1) return null;
    return {
        namespace: ref.slice(0, dot),
        key: ref.slice(dot + 1),
    };
}
